/**
 * src/lib/offline/cfoCache.js — offline cache for the Analytics CFO finance data.
 *
 * Analytics pulls shared domain data (tasks/projects/crm) from AppDataContext —
 * that's already cached — PLUS eight CFO-specific collections it fetches on its
 * own (invoices, supplier invoices, POs, PO lines, bank transactions, deal
 * forecasts, change orders, and the reconciliation summary). Those were NOT
 * cached, so every Analytics subtab went blank offline.
 *
 * This module is the single source of truth for those collections: one fetcher
 * list reused by BOTH useAnalyticsData (online write-through / offline hydrate)
 * AND prepareOffline (the warm-all-tabs button), so the keys can never drift.
 *
 * Reconciliation is an OBJECT, not an array; the entity cache stores arrays, so
 * it's wrapped as a single-row array under its own key and unwrapped on read.
 */

import { reconcileCollection } from './entityCache';
import { fetchInvoices, fetchSupplierInvoices, fetchAllPurchaseOrders, fetchAllPOLines } from '../api/analytics';
import { fetchBankTransactions, fetchDealForecasts, fetchChangeOrders, fetchUnmatched, fetchTreasurySummary } from '../api/cfo';

export const EMPTY_RECONCILIATION = { unmatched_inflows: [], unmatched_outflows: [], ar_overdue: [], ap_aging: [], total_unmatched: 0 };

// [ cacheKey, fetcher, cfoDataField ] — array collections.
const ARRAY_COLLECTIONS = [
  ['cfoInvoices', fetchInvoices, 'invoices'],
  ['cfoSupplierInvoices', fetchSupplierInvoices, 'supplierInvoices'],
  ['cfoPurchaseOrders', fetchAllPurchaseOrders, 'purchaseOrders'],
  ['cfoPoLines', fetchAllPOLines, 'poLines'],
  ['cfoBankTransactions', fetchBankTransactions, 'bankTransactions'],
  ['cfoDealForecasts', fetchDealForecasts, 'dealForecasts'],
  ['cfoChangeOrders', fetchChangeOrders, 'changeOrders'],
];

const RECONCILIATION_KEY = 'cfoReconciliation';
// TICKET-1332: the Real Treasury summary over ALL bank lines (server-side); the bank list
// above is only the newest 100 rows and must not drive the total.
const TREASURY_KEY = 'cfoTreasury';

/** Every IndexedDB collection key this module manages (for diagnostics / tests). */
export const CFO_COLLECTION_KEYS = [...ARRAY_COLLECTIONS.map((c) => c[0]), RECONCILIATION_KEY, TREASURY_KEY];

/**
 * Fetch + reconcile all CFO collections and return the assembled cfoData shape.
 * write-through to cache when online, hydrate from cache when offline — same
 * policy as every other domain (reconcileCollection never caches an empty/failed
 * result over a good one).
 * @param {boolean} online
 * @returns {Promise<{invoices:any[],supplierInvoices:any[],purchaseOrders:any[],poLines:any[],bankTransactions:any[],dealForecasts:any[],changeOrders:any[],reconciliation:object}>}
 */
export async function loadCfoData(online) {
  const arrayPairs = await Promise.all(
    ARRAY_COLLECTIONS.map(async ([key, fetcher, field]) => {
      const fetched = await fetcher().catch(() => { /* spine-emit-ok: best-effort warm; offline/transient failure falls back to cache via reconcile */ return []; });
      const rows = await reconcileCollection(key, Array.isArray(fetched) ? fetched : [], online);
      return [field, rows];
    }),
  );

  // Reconciliation summary (object) — only cache a real one (don't clobber a
  // good cached summary with the empty sentinel on a transient failure).
  const recFetched = await fetchUnmatched().catch(() => { /* spine-emit-ok: best-effort warm; offline falls back to cached reconciliation */ return EMPTY_RECONCILIATION; });
  const recIsReal = recFetched && typeof recFetched === 'object' && !Array.isArray(recFetched) && (recFetched.total_unmatched || 0) >= 0
    && recFetched !== EMPTY_RECONCILIATION;
  // The cache store keys rows by `id` (rows without one are dropped), so wrap
  // the id-less reconciliation summary with a stable synthetic id. The extra
  // field is harmless to every consumer (they read total_unmatched / *_aging).
  const recRows = await reconcileCollection(RECONCILIATION_KEY, recIsReal ? [{ id: 'reconciliation', ...recFetched }] : [], online);
  const reconciliation = (recRows && recRows[0]) || EMPTY_RECONCILIATION;

  // Treasury summary (object, same caching rule as reconciliation: never cache a failure).
  const tFetched = await fetchTreasurySummary().catch(() => { /* spine-emit-ok: best-effort warm; offline falls back to the cached summary */ return null; });
  const tIsReal = tFetched && typeof tFetched === 'object' && Number.isFinite(tFetched.total_transactions);
  const tRows = await reconcileCollection(TREASURY_KEY, tIsReal ? [{ id: 'treasury', ...tFetched }] : [], online);
  const treasury = (tRows && tRows[0]) || null;

  return { ...Object.fromEntries(arrayPairs), reconciliation, treasury };
}
